import React from 'react';

import { Button, Group, Stack, Text, Title, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import useResyncGoogleCalendarIntegration from 'hooks/googleCalendarIntegration/useResyncGoogleCalendarIntegration';
import { GoogleCalendarIntegration } from 'types';
import notificationHandler from 'utils/notifications';

import ConflictCheckingCard from './components/ConflictCheckingCard';
import DeleteIntegrationModal from './components/DeleteIntegrationModal';
import SynchronizationCard from './components/SynchronizationCard';

interface ExistingGoogleCalendarIntegrationProps {
  googleCalendarIntegration: GoogleCalendarIntegration;
}

const ExistingGoogleCalendarIntegration = ({
  googleCalendarIntegration,
}: ExistingGoogleCalendarIntegrationProps): JSX.Element => {
  const [isDeleteModalOpened, { close: closeDeleteModal, open: openDeleteModal }] = useDisclosure(false);

  const { successNotification, errorNotification } = notificationHandler();

  const resyncMutation = useResyncGoogleCalendarIntegration();

  const handleResync = (): void => {
    resyncMutation.mutate(googleCalendarIntegration.googleCalendarIntegrationId, {
      onError: error => {
        errorNotification('Calendar refetch failed', error.response?.data.message);
      },
      onSuccess: () => {
        successNotification('Calendars refetched successfully');
      },
    });
  };

  return (
    <Stack spacing='xl' my='xl'>
      <Stack spacing='xs'>
        <Group position='apart'>
          <Title order={2}>Google Calendar Integration</Title>
          <Tooltip label='Finds all calendars associated with your google account and brings them into Kilo.'>
            <Button variant='outline' onClick={handleResync} loading={resyncMutation.isLoading}>
              Fetch Calendars
            </Button>
          </Tooltip>
        </Group>
        <Text color='dimmed'>
          Use this feature to customize how Kilo interacts with your Google calendar.
        </Text>
      </Stack>

      <SynchronizationCard googleCalendarIntegration={googleCalendarIntegration} />

      <ConflictCheckingCard
        googleCalendarIntegrationId={googleCalendarIntegration.googleCalendarIntegrationId}
      />

      <Button variant='light' color='red' size='sm' onClick={openDeleteModal}>
        Delete Integration
      </Button>

      <DeleteIntegrationModal
        opened={isDeleteModalOpened}
        onClose={closeDeleteModal}
        googleCalendarIntegration={googleCalendarIntegration}
      />
    </Stack>
  );
};

export default ExistingGoogleCalendarIntegration;
